import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import type { PlanAssumption, BuildPlan } from '../../types';

const CATEGORY_COLORS: Record<PlanAssumption['category'], string> = {
  scope: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  behavior: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  content: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  integration: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  design: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
};

interface AssumptionReviewProps {
  assumptions: PlanAssumption[];
  buildPlan: BuildPlan | null;
  onSuggestChanges: (comments: Record<string, string>) => void;
  onApprovePlan: (comments?: Record<string, string>) => void;
  isProcessing: boolean;
}

export function AssumptionReview({
  assumptions,
  buildPlan,
  onSuggestChanges,
  onApprovePlan,
  isProcessing,
}: AssumptionReviewProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [editValue, setEditValue] = useState('');

  // Merge any in-progress edit into comments to get the complete set
  const getCommentsWithPending = useCallback(() => {
    if (editingId && editValue.trim()) {
      return { ...comments, [editingId]: editValue.trim() };
    }
    return comments;
  }, [comments, editingId, editValue]);

  const allComments = getCommentsWithPending();
  const hasComments = Object.values(allComments).some((c) => c.trim().length > 0);

  const handleStartEdit = useCallback(
    (id: string) => {
      setEditingId(id);
      setEditValue(comments[id] || '');
    },
    [comments],
  );

  const handleSaveEdit = useCallback(() => {
    if (editingId && editValue.trim()) {
      setComments((prev) => ({ ...prev, [editingId]: editValue.trim() }));
    }
    setEditingId(null);
    setEditValue('');
  }, [editingId, editValue]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  if (assumptions.length === 0 && !buildPlan) return null;

  return (
    <div className="flex-1 flex flex-col p-6 overflow-y-auto">
      <div className="max-w-lg mx-auto w-full space-y-6 flex-1 flex flex-col">
        {buildPlan && (
          <div className="space-y-1">
            <h3 className="text-base font-semibold">{buildPlan.name}</h3>
            <p className="text-sm text-muted-foreground">{buildPlan.description}</p>
            <p className="text-xs text-muted-foreground">{buildPlan.steps.length} steps planned</p>
          </div>
        )}

        {assumptions.length === 0 && isProcessing && (
          <div className="flex items-center gap-3 py-8 justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p className="text-sm">Generating assumptions for your review...</p>
          </div>
        )}

        {assumptions.length > 0 && (
          <>
            <p className="text-sm font-medium">
              Review the assumptions below. Click the pencil icon to comment on any that need
              correction.
            </p>

            <div className="space-y-2 flex-1">
              {assumptions.map((a) => (
                <div
                  key={a.id}
                  className="flex items-start gap-2 p-3 rounded-md border bg-background"
                >
                  <Badge
                    variant="secondary"
                    className={`text-[10px] px-1.5 py-0 ${CATEGORY_COLORS[a.category]}`}
                  >
                    {a.category}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{a.assumption}</p>
                    {comments[a.id] && editingId !== a.id && (
                      <p className="text-xs text-primary mt-1">Your comment: {comments[a.id]}</p>
                    )}
                    {editingId === a.id && (
                      <div className="mt-1 flex gap-1">
                        <Textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="What should change?"
                          className="text-xs min-h-[40px] flex-1"
                          autoFocus
                        />
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={handleSaveEdit}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {editingId !== a.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={() => handleStartEdit(a.id)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {assumptions.length > 0 && (
          <div className="flex items-center gap-2 pt-2">
            {hasComments ? (
              <Button
                size="lg"
                onClick={() => onSuggestChanges(allComments)}
                disabled={isProcessing}
                className="w-full"
              >
                Submit Changes
              </Button>
            ) : (
              <Button
                size="lg"
                onClick={() => onApprovePlan()}
                disabled={isProcessing}
                className="w-full"
              >
                Approve Plan
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
